import { colorPalette, getLighterShade } from "@/pages/Board/utils";
import {
  BoardState,
  Column,
  LoadingState,
  PaginationState,
  SubGroupData,
} from "./interface";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { set } from "react-hook-form";

const initialState: BoardState = {
  // Core state
  selectedItem: "Dashboard",
  viewType: "dashboard",
  searchQuery: "",
  debouncedSearchQuery: "",
  createTask: false,
  selectedColumnStatus: null,
  dueDateDateForCalendarView: null,
  // Filters
  selectedTags: [],
  selectedAvatars: [],
  selectedStatuses: [],
  selectedPriorities: [],
  selectedProjects: [],
  selectedDueDate: "",
  selectedStartDate: "",
  selectedUpdatedDate: "",
  selectedCreatedBy: [],
  dueDateStart: "",
  dueDateEnd: "",
  startDateStart: "",
  startDateEnd: "",
  updatedAtStart: "",
  updatedAtEnd: "",
  isRecurring: false,

  // Grouping
  groupBy: "Status",
  subGroupBy: "None",
  sortOrder: "Ascending",
  showGroupOptions: false,

  // UI Preferences
  drawerOpen: true,
  minimizedColumns: {},
  expandedDetailPanels: [],

  // API Data
  statusList: [],
  projectDetails: null,
  listOfBugs: [],
  listCount: 0,
  tagList: [],
  companyMemberData: [],
  projects: [],

  // Dynamic Board Data
  columns: {},
  columnOrder: [],
  subGroupData: {},
  groupingData: {},
  subGroupingData: {},

  // Loading States
  loadingStatuses: {},
  initialLoading: true,
  isStatusLoading: false,
  isBugsLoading: false,
  isBugsFetching: false,
  isProjectLoading: false,
  tagListLoading: false,

  // Pagination
  pagination: {},
  hasMoreMap: {},

  // Form State
  isAddingColumn: false,
  newColumnTitle: "",
  isDeleteConfirmOpen: false,
  columnToDelete: null,

  // Technical State
  isDragging: false,
  scrollPositions: {},

  //Sprint
  activeSprintIds: [],
  activeSprints: [],
  allSprints: [],
  selectedSprintData: null,

  //Backlog
  backlogViewType: "table",
};

const boardSlice = createSlice({
  name: "board",
  initialState,
  reducers: {
    // Core Actions
    setSelectedItem: (state, action: PayloadAction<string>) => {
      state.selectedItem = action.payload;
    },
    setCreateTask: (state, action: PayloadAction<boolean>) => {
      state.createTask = action.payload;
    },
    setSelectedColumnStatus: (state, action: PayloadAction<string | null>) => {
      state.selectedColumnStatus = action.payload;
    },
    setViewType: (state, action: PayloadAction<string>) => {
      state.viewType = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setDueDateDateForCalendarView: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.dueDateDateForCalendarView = action.payload;
    },
    setDebouncedSearchQuery: (state, action: PayloadAction<string>) => {
      state.debouncedSearchQuery = action.payload;
    },

    // Filter Actions
    setSelectedTags: (state, action: PayloadAction<string[]>) => {
      state.selectedTags = action.payload;
    },
    toggleTag: (state, action: PayloadAction<string>) => {
      const tagId = action.payload;
      if (state.selectedTags.includes(tagId)) {
        state.selectedTags = state.selectedTags.filter((id) => id !== tagId);
      } else {
        state.selectedTags.push(tagId);
      }
    },
    setSelectedAvatars: (state, action: PayloadAction<string[]>) => {
      state.selectedAvatars = action.payload;
    },
    toggleAvatar: (state, action: PayloadAction<string>) => {
      const avatarId = action.payload;
      if (state.selectedAvatars.includes(avatarId)) {
        state.selectedAvatars = state.selectedAvatars.filter(
          (id) => id !== avatarId
        );
      } else {
        state.selectedAvatars.push(avatarId);
      }
    },
    setSelectedStatuses: (state, action: PayloadAction<string[]>) => {
      state.selectedStatuses = action.payload;
    },
    setSelectedPriorities: (state, action: PayloadAction<string[]>) => {
      state.selectedPriorities = action.payload;
    },
    // Add project filter action
    setSelectedProjects: (state, action: PayloadAction<string[]>) => {
      state.selectedProjects = action.payload;
    },
    setDueDateFilter: (
      state,
      action: PayloadAction<{
        selectedDueDate: string;
        dueDateStart: string;
        dueDateEnd: string;
      }>
    ) => {
      state.selectedDueDate = action.payload.selectedDueDate;
      state.dueDateStart = action.payload.dueDateStart;
      state.dueDateEnd = action.payload.dueDateEnd;
    },
    setStartDateFilter: (
      state,
      action: PayloadAction<{
        selectedStartDate: string;
        startDateStart: string;
        startDateEnd: string;
      }>
    ) => {
      state.selectedStartDate = action.payload.selectedStartDate;
      state.startDateStart = action.payload.startDateStart;
      state.startDateEnd = action.payload.startDateEnd;
    },
    setUpdatedDateFilter: (
      state,
      action: PayloadAction<{
        selectedUpdatedDate: string;
        updatedAtStart: string;
        updatedAtEnd: string;
      }>
    ) => {
      state.selectedUpdatedDate = action.payload.selectedUpdatedDate;
      state.updatedAtStart = action.payload.updatedAtStart;
      state.updatedAtEnd = action.payload.updatedAtEnd;
    },
    setSelectedCreatedBy: (state, action: PayloadAction<string[]>) => {
      state.selectedCreatedBy = action.payload;
    },
    setIsRecurring: (state, action: PayloadAction<boolean>) => {
      state.isRecurring = action.payload;
    },
    clearAllFilters: (state) => {
      state.selectedTags = [];
      state.selectedAvatars = [];
      state.selectedStatuses = [];
      state.selectedPriorities = [];
      state.selectedProjects = [];
      state.selectedDueDate = "";
      state.selectedStartDate = "";
      state.selectedUpdatedDate = "";
      state.dueDateStart = "";
      state.dueDateEnd = "";
      state.startDateStart = "";
      state.startDateEnd = "";
      state.updatedAtStart = "";
      state.updatedAtEnd = "";
      state.searchQuery = "";
      state.debouncedSearchQuery = "";
      state.isRecurring = false;
      state.selectedCreatedBy = [];
    },

    // Grouping Actions
    setGroupBy: (state, action: PayloadAction<string>) => {
      state.groupBy = action.payload;
      // Reset related state when grouping changes
      state.columns = {};
      state.columnOrder = [];
      state.subGroupData = {};
      state.loadingStatuses = {};
      state.pagination = {};
      state.hasMoreMap = {};
    },
    setSubGroupBy: (state, action: PayloadAction<string>) => {
      state.subGroupBy = action.payload;
      // Reset related state when sub-grouping changes
      state.columns = {};
      state.columnOrder = [];
      state.subGroupData = {};
      state.loadingStatuses = {};
      state.pagination = {};
      state.hasMoreMap = {};
    },
    setSortOrder: (state, action: PayloadAction<string>) => {
      state.sortOrder = action.payload;
    },
    setShowGroupOptions: (state, action: PayloadAction<boolean>) => {
      state.showGroupOptions = action.payload;
    },

    // UI Actions
    setDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.drawerOpen = action.payload;
    },
    setMinimizedColumn: (
      state,
      action: PayloadAction<{
        columnId: string;
        isMinimized: boolean;
      }>
    ) => {
      state.minimizedColumns[action.payload.columnId] =
        action.payload.isMinimized;
    },
    toggleExpandedDetailPanel: (state, action: PayloadAction<string>) => {
      const panelId = action.payload;
      if (state.expandedDetailPanels.includes(panelId)) {
        state.expandedDetailPanels = state.expandedDetailPanels.filter(
          (id) => id !== panelId
        );
      } else {
        state.expandedDetailPanels.push(panelId);
      }
    },

    // API Data Actions
    setStatusList: (state, action: PayloadAction<any[]>) => {
      state.statusList = action.payload;
    },
    setProjectDetails: (state, action: PayloadAction<any>) => {
      state.projectDetails = action.payload;
    },
    setListOfBugs: (state, action: PayloadAction<any[]>) => {
      state.listOfBugs = action.payload;
    },
    setListCount: (state, action: PayloadAction<number>) => {
      state.listCount = action.payload;
    },
    setTagList: (state, action: PayloadAction<any[]>) => {
      state.tagList = action.payload;
    },
    setCompanyMemberData: (state, action: PayloadAction<any[]>) => {
      state.companyMemberData = action.payload;
    },
    setProjects: (state, action: PayloadAction<any[]>) => {
      state.projects = action.payload;
    },

    // Board Data Actions
    setColumns: (state, action: PayloadAction<Record<string, Column>>) => {
      state.columns = action.payload;
    },
    updateColumn: (
      state,
      action: PayloadAction<{
        columnId: string;
        column: Partial<Column>;
      }>
    ) => {
      if (!state.columns[action.payload.columnId]) {
        state.columns[action.payload.columnId] = {
          id: action.payload.columnId,
          title: "",
          color: "",
          icon: "",
          tasks: [],
          totalCount: 0,
          description: "",
          hidden: false,
          ...action.payload.column,
        };
      } else {
        state.columns[action.payload.columnId] = {
          ...state.columns[action.payload.columnId],
          ...action.payload.column,
        };
      }
    },
    setColumnOrder: (state, action: PayloadAction<string[]>) => {
      state.columnOrder = action.payload;
    },
    setSubGroupData: (state, action: PayloadAction<SubGroupData>) => {
      state.subGroupData = action.payload;
    },
    setSubGroupColumnData: (
      state,
      action: PayloadAction<{
        subGroupId: string;
        columnId: string;
        tasks: any[];
        totalCount: number;
        pagination: PaginationState;
        hasMore: boolean;
        loadingState: LoadingState;
        append?: boolean;
      }>
    ) => {
      const {
        subGroupId,
        columnId,
        tasks,
        totalCount,
        pagination,
        hasMore,
        loadingState,
        append = false,
      } = action.payload;

      // Initialize sub-group if it doesn't exist
      if (!state.subGroupData[subGroupId]) {
        const subGroupInfo = state.subGroupingData[subGroupId];
        if (subGroupInfo) {
          // Create ordered column order based on groupingData order
          const orderedColumnIds = Object.keys(state.groupingData).filter(
            (id) => state.groupingData[id] && !state.groupingData[id].hidden
          );

          state.subGroupData[subGroupId] = {
            id: subGroupId,
            title: subGroupInfo.title,
            color: subGroupInfo.color,
            icon: subGroupInfo.icon,
            type: subGroupInfo.type,
            columns: {},
            columnOrder: orderedColumnIds, // Use the ordered column IDs
            pagination: {},
            hasMore: {},
            loadingStatuses: {},
          };
        }
      }

      // Initialize column if it doesn't exist
      if (!state.subGroupData[subGroupId].columns[columnId]) {
        const columnInfo = state.groupingData[columnId];
        if (columnInfo) {
          state.subGroupData[subGroupId].columns[columnId] = {
            id: columnId,
            title: columnInfo.title,
            color: columnInfo.color,
            icon: columnInfo.icon,
            tasks: [],
            totalCount: 0,
            description: columnInfo.description || "",
            hidden: columnInfo.hidden || false,
          };

          // Only add to columnOrder if it's not already there and maintain the order
          if (!state.subGroupData[subGroupId].columnOrder.includes(columnId)) {
            // Find the correct position based on groupingData order
            const orderedColumnIds = Object.keys(state.groupingData).filter(
              (id) => state.groupingData[id] && !state.groupingData[id].hidden
            );

            // Update the entire columnOrder to match groupingData order
            state.subGroupData[subGroupId].columnOrder = orderedColumnIds;
          }
        }
      }

      // Update the column data
      state.subGroupData[subGroupId].columns[columnId].tasks = tasks;
      state.subGroupData[subGroupId].columns[columnId].totalCount = totalCount;
      state.subGroupData[subGroupId].pagination[columnId] = pagination;
      state.subGroupData[subGroupId].hasMore[columnId] = hasMore;
      state.subGroupData[subGroupId].loadingStatuses[columnId] = loadingState;
    },
    setSubGroupLoadingState: (
      state,
      action: PayloadAction<{
        subGroupId: string;
        columnId: string;
        loadingState: LoadingState;
      }>
    ) => {
      const { subGroupId, columnId, loadingState } = action.payload;

      if (
        state.subGroupData[subGroupId] &&
        state.subGroupData[subGroupId].loadingStatuses
      ) {
        state.subGroupData[subGroupId].loadingStatuses[columnId] = loadingState;
      }
    },
    updateSubGroupData: (
      state,
      action: PayloadAction<{
        subGroupId: string;
        data: Partial<SubGroupData[string]>;
      }>
    ) => {
      if (state.subGroupData[action.payload.subGroupId]) {
        state.subGroupData[action.payload.subGroupId] = {
          ...state.subGroupData[action.payload.subGroupId],
          ...action.payload.data,
        };
      }
    },
    setGroupingData: (state, action: PayloadAction<Record<string, any>>) => {
      state.groupingData = action.payload;
    },
    setSubGroupingData: (state, action: PayloadAction<Record<string, any>>) => {
      state.subGroupingData = action.payload;
    },

    // Loading Actions
    setLoadingStatus: (
      state,
      action: PayloadAction<{
        key: string;
        loading: LoadingState;
      }>
    ) => {
      state.loadingStatuses[action.payload.key] = action.payload.loading;
    },
    setInitialLoading: (state, action: PayloadAction<boolean>) => {
      state.initialLoading = action.payload;
    },
    setIsBugsLoading: (state, action: PayloadAction<boolean>) => {
      state.isBugsLoading = action.payload;
    },
    setIsBugsFetching: (state, action: PayloadAction<boolean>) => {
      state.isBugsFetching = action.payload;
    },

    // Pagination Actions
    setPagination: (
      state,
      action: PayloadAction<{
        key: string;
        pagination: PaginationState;
      }>
    ) => {
      state.pagination[action.payload.key] = action.payload.pagination;
    },
    setHasMore: (
      state,
      action: PayloadAction<{
        key: string;
        hasMore: boolean;
      }>
    ) => {
      state.hasMoreMap[action.payload.key] = action.payload.hasMore;
    },

    // Form Actions
    setIsAddingColumn: (state, action: PayloadAction<boolean>) => {
      state.isAddingColumn = action.payload;
    },
    setNewColumnTitle: (state, action: PayloadAction<string>) => {
      state.newColumnTitle = action.payload;
    },
    setDeleteConfirmModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        column?: { id: string; title: string } | null;
      }>
    ) => {
      state.isDeleteConfirmOpen = action.payload.open;
      state.columnToDelete = action.payload.column || null;
    },

    // Technical Actions
    setIsDragging: (state, action: PayloadAction<boolean>) => {
      state.isDragging = action.payload;
    },
    setScrollPosition: (
      state,
      action: PayloadAction<{
        columnId: string;
        position: number;
      }>
    ) => {
      state.scrollPositions[action.payload.columnId] = action.payload.position;
    },

    // Task Update Action
    updateTask: (
      state,
      action: PayloadAction<{
        taskId: string;
        updatedTask: any | null;
        newColumnId?: string;
        subGroupId?: string;
      }>
    ) => {
      const { taskId, updatedTask, newColumnId, subGroupId } = action.payload;

      if (state.subGroupBy !== "None" && state.subGroupData) {
        // Handle sub-group task update for ALL sub-groups
        Object.keys(state.subGroupData).forEach((currentSubGroupId) => {
          const subGroup = state.subGroupData[currentSubGroupId];
          if (!subGroup || !subGroup.columns) return;

          Object.keys(subGroup.columns).forEach((columnId) => {
            const column = subGroup.columns[columnId];
            if (!column || !column.tasks) return;

            const taskIndex = column.tasks.findIndex(
              (t: any) => t._id === taskId
            );

            if (taskIndex >= 0) {
              if (updatedTask === null) {
                // Remove task
                column.tasks.splice(taskIndex, 1);
                column.totalCount = Math.max((column.totalCount || 0) - 1, 0);
              } else if (newColumnId && newColumnId !== columnId) {
                // Move task to different column
                const [movedTask] = column.tasks.splice(taskIndex, 1);
                column.totalCount = Math.max((column.totalCount || 0) - 1, 0);

                if (subGroup.columns[newColumnId]) {
                  // Add at the beginning using unshift
                  subGroup.columns[newColumnId].tasks.unshift({
                    ...movedTask,
                    ...updatedTask,
                  });
                  subGroup.columns[newColumnId].totalCount =
                    (subGroup.columns[newColumnId].totalCount || 0) + 1;
                }
              } else {
                // Update task in place
                column.tasks[taskIndex] = {
                  ...column.tasks[taskIndex],
                  ...updatedTask,
                };
              }
            }
          });
        });
      } else {
        // Handle regular column task update (existing logic)
        let sourceColumnId: string | null = null;
        let taskIndex = -1;

        // Find current column and task index
        for (const columnId in state.columns) {
          const column = state.columns[columnId];
          const index = column.tasks.findIndex((t: any) => t._id === taskId);

          if (index >= 0) {
            sourceColumnId = columnId;
            taskIndex = index;
            break;
          }
        }

        if (sourceColumnId && state.columns[sourceColumnId]) {
          const sourceColumn = state.columns[sourceColumnId];

          if (updatedTask === null) {
            // Delete task
            sourceColumn.tasks = sourceColumn.tasks.filter(
              (t: any) => t._id !== taskId
            );
            sourceColumn.totalCount = Math.max(
              (sourceColumn.totalCount || 0) - 1,
              0
            );
          } else if (newColumnId && newColumnId !== sourceColumnId) {
            // Move task to different column
            const updatedSourceTasks = sourceColumn.tasks.filter(
              (t: any) => t._id !== taskId
            );
            sourceColumn.tasks = updatedSourceTasks;
            sourceColumn.totalCount = Math.max(
              (sourceColumn.totalCount || 0) - 1,
              0
            );

            if (state.columns[newColumnId]) {
              const existsInDestination = state.columns[newColumnId].tasks.some(
                (t: any) => t._id === taskId
              );

              if (!existsInDestination) {
                // Add at the beginning using unshift
                state.columns[newColumnId].tasks.unshift(updatedTask);
                state.columns[newColumnId].totalCount =
                  (state.columns[newColumnId].totalCount || 0) + 1;
              } else {
                state.columns[newColumnId].tasks = state.columns[
                  newColumnId
                ].tasks.map((t: any) =>
                  t._id === taskId ? { ...t, ...updatedTask } : t
                );
              }
            }
          } else {
            // Update task in place
            sourceColumn.tasks[taskIndex] = {
              ...sourceColumn.tasks[taskIndex],
              ...updatedTask,
            };
          }
        }
      }
    },

    // Reset Actions
    resetBoardState: (state) => {
      state.columns = {};
      state.columnOrder = [];
      state.subGroupData = {};
      state.loadingStatuses = {};
      state.pagination = {};
      state.hasMoreMap = {};
      state.initialLoading = true;
      // state.statusList = [];
    },
    createSubGroupingData: (
      state,
      action: PayloadAction<{
        subGroupBy: string;
        statusList: any[];
        companyMemberData: any[];
        tagList: any[];
        projects: any[]; // Add projects parameter
        // Add filter parameters
        filters?: {
          selectedStatuses?: string[];
          selectedAvatars?: string[];
          selectedTags?: string[];
          selectedPriorities?: string[];
          selectedProjects?: string[]; // Add selectedProjects filter
        };
      }>
    ) => {
      const {
        subGroupBy,
        statusList,
        companyMemberData,
        tagList,
        projects,
        filters,
      } = action.payload;

      if (subGroupBy === "None") {
        state.subGroupingData = {};
        return;
      }

      const data: Record<string, any> = {};

      switch (subGroupBy) {
        case "Status":
          if (statusList?.length > 0) {
            const sortedItems = [...statusList].sort(
              (a: any, b: any) => a.boardIndex - b.boardIndex
            );

            // Filter statusList based on selectedStatuses if filter is applied
            const filteredItems = filters?.selectedStatuses?.length
              ? sortedItems.filter((status) =>
                  filters.selectedStatuses!.includes(status._id)
                )
              : sortedItems;

            filteredItems.forEach((status: any) => {
              data[status._id] = {
                id: status._id,
                title: status.status,
                color: status.color,
                icon: status.icon,
                type: "status",
                description: status.description || "",
                hidden: status.hidden || false,
              };
            });
          }
          break;

        case "Assignee":
          if (companyMemberData?.length > 0) {
            // Filter companyMemberData based on selectedAvatars if filter is applied
            const filteredMembers = filters?.selectedAvatars?.length
              ? companyMemberData.filter((member) =>
                  filters.selectedAvatars!.includes(member._id)
                )
              : companyMemberData;

            filteredMembers.forEach((member, index) => {
              data[member._id] = {
                id: member._id,
                title: member.fullName,
                color: getLighterShade(
                  colorPalette[index % colorPalette.length]
                ),
                icon: "mdi:account",
                type: "assignee",
              };
            });
          }
          break;

        case "Priority":
          const priorities = ["Low", "Medium", "High", "Highest"];
          const priorityColors = {
            Low: "#4CAF50",
            Medium: "#FF9800",
            High: "#F44336",
            Highest: "#9C27B0",
          };

          // Filter priorities based on selectedPriorities if filter is applied
          const filteredPriorities = filters?.selectedPriorities?.length
            ? priorities.filter((priority) =>
                filters.selectedPriorities!.includes(priority)
              )
            : priorities;

          filteredPriorities.forEach((priority) => {
            data[priority.toLowerCase()] = {
              id: priority.toLowerCase(),
              title: priority,
              color: priorityColors[priority as keyof typeof priorityColors],
              icon: "mdi:flag",
              type: "priority",
            };
          });
          break;

        case "Project":
          if (projects?.length > 0) {
            // Filter projects based on selectedProjects if filter is applied
            const filteredProjects = filters?.selectedProjects?.length
              ? projects.filter((project) =>
                  filters.selectedProjects!.includes(project._id)
                )
              : projects;

            filteredProjects.forEach((project, index) => {
              data[project._id] = {
                id: project._id,
                title: project.name,
                color:
                  project.icon ||
                  getLighterShade(colorPalette[index % colorPalette.length]),
                icon: "mdi:folder",
                type: "project",
                projectKey: project.projectKey,
                code: project.code,
              };
            });
          }
          break;

        case "Tags":
          if (tagList?.length > 0) {
            // Filter tagList based on selectedTags if filter is applied
            const filteredTags = filters?.selectedTags?.length
              ? tagList.filter(
                  (tag) =>
                    filters.selectedTags!.includes(tag.tagName) ||
                    filters.selectedTags!.includes(tag._id)
                )
              : tagList;

            filteredTags.forEach((tag, index) => {
              data[tag._id] = {
                id: tag._id,
                title: tag.tagName,
                color: getLighterShade(
                  colorPalette[index % colorPalette.length]
                ),
                icon: "mdi:tag",
                type: "tag",
                tagName: tag.tagName,
              };
            });
          }
          break;

        case "Task type":
          const taskTypes = ["Bug", "Story", "Epic"];
          const taskTypeColors = {
            Bug: "#F44336",
            Story: "#4CAF50",
            Epic: "#2196F3",
          };
          taskTypes.forEach((type) => {
            data[type.toLowerCase()] = {
              id: type.toLowerCase(),
              title: type,
              color: taskTypeColors[type as keyof typeof taskTypeColors],
              icon: "mdi:clipboard-text",
              type: "taskType",
            };
          });
          break;

        case "Due date":
          const dueDateGroups = [
            {
              id: "overdue",
              title: "Overdue",
              color: "#F44336",
              icon: "mdi:calendar-alert",
            },
            {
              id: "today",
              title: "Due Today",
              color: "#FF9800",
              icon: "mdi:calendar-today",
            },
            {
              id: "this_week",
              title: "This Week",
              color: "#2196F3",
              icon: "mdi:calendar-week",
            },
            {
              id: "next_week",
              title: "Next Week",
              color: "#4CAF50",
              icon: "mdi:calendar-week",
            },
            {
              id: "later",
              title: "Later",
              color: "#9E9E9E",
              icon: "mdi:calendar-clock",
            },
            {
              id: "no_due_date",
              title: "No Due Date",
              color: "#607D8B",
              icon: "mdi:calendar-blank",
            },
          ];
          dueDateGroups.forEach((group) => {
            data[group.id] = {
              ...group,
              type: "dueDate",
            };
          });
          break;

        // Add Start Date grouping case
        case "Start date":
          const startDateGroups = [
            {
              id: "overdue_start",
              title: "Overdue Start",
              color: "#F44336",
              icon: "mdi:calendar-alert",
            },
            {
              id: "today_start",
              title: "Starting Today",
              color: "#FF9800",
              icon: "mdi:calendar-today",
            },
            {
              id: "this_week_start",
              title: "Starting This Week",
              color: "#2196F3",
              icon: "mdi:calendar-week",
            },
            {
              id: "next_week_start",
              title: "Starting Next Week",
              color: "#4CAF50",
              icon: "mdi:calendar-week",
            },
            {
              id: "later_start",
              title: "Starting Later",
              color: "#9E9E9E",
              icon: "mdi:calendar-clock",
            },
            {
              id: "no_start_date",
              title: "No Start Date",
              color: "#607D8B",
              icon: "mdi:calendar-blank",
            },
          ];
          startDateGroups.forEach((group) => {
            data[group.id] = {
              ...group,
              type: "startDate",
            };
          });
          break;

        default:
          break;
      }

      state.subGroupingData = data;
    },
    createGroupingData: (
      state,
      action: PayloadAction<{
        groupBy: string;
        statusList: any[];
        companyMemberData: any[];
        tagList: any[];
        projects: any[]; // Add projects parameter
        // Add filter parameters
        filters?: {
          selectedStatuses?: string[];
          selectedAvatars?: string[];
          selectedTags?: string[];
          selectedPriorities?: string[];
          selectedProjects?: string[]; // Add selectedProjects filter
        };
      }>
    ) => {
      const {
        groupBy,
        statusList,
        companyMemberData,
        tagList,
        projects,
        filters,
      } = action.payload;

      const data: Record<string, any> = {};

      switch (groupBy) {
        case "None":
          break;
        case "Status":
          if (statusList?.length > 0) {
            const sortedItems = [...statusList].sort(
              (a: any, b: any) => a.boardIndex - b.boardIndex
            );

            // Filter statusList based on selectedStatuses if filter is applied
            const filteredItems = filters?.selectedStatuses?.length
              ? sortedItems.filter((status) =>
                  filters.selectedStatuses!.includes(status._id)
                )
              : sortedItems;

            filteredItems.forEach((status: any) => {
              data[status._id] = {
                id: status._id,
                title: status.status,
                color: status.color || "#4285F2",
                icon: status.icon,
                type: "status",
                description: status.description || "",
                hidden: status.hidden || false,
              };
            });
          }
          break;

        case "Assignee":
          if (companyMemberData?.length > 0) {
            // Filter companyMemberData based on selectedAvatars if filter is applied
            const filteredMembers = filters?.selectedAvatars?.length
              ? companyMemberData.filter((member) =>
                  filters.selectedAvatars!.includes(member._id)
                )
              : companyMemberData;

            filteredMembers.forEach((member, index) => {
              data[member._id] = {
                id: member._id,
                title: member.fullName,
                color: getLighterShade(
                  colorPalette[index % colorPalette.length]
                ),
                icon: "mdi:account",
                type: "assignee",
              };
            });
          }
          break;

        case "Priority":
          const priorities = ["Low", "Medium", "High", "Highest"];
          const priorityColors = {
            Low: "#4CAF50",
            Medium: "#FF9800",
            High: "#F44336",
            Highest: "#9C27B0",
          };

          // Filter priorities based on selectedPriorities if filter is applied
          const filteredPriorities = filters?.selectedPriorities?.length
            ? priorities.filter((priority) =>
                filters.selectedPriorities!.includes(priority)
              )
            : priorities;

          filteredPriorities.forEach((priority) => {
            data[priority.toLowerCase()] = {
              id: priority.toLowerCase(),
              title: priority,
              color: priorityColors[priority as keyof typeof priorityColors],
              icon: "mdi:flag",
              type: "priority",
            };
          });
          break;

        case "Project":
          if (projects?.length > 0) {
            // Filter projects based on selectedProjects if filter is applied
            const filteredProjects = filters?.selectedProjects?.length
              ? projects.filter((project) =>
                  filters.selectedProjects!.includes(project._id)
                )
              : projects;

            filteredProjects.forEach((project, index) => {
              data[project._id] = {
                id: project._id,
                title: project.name,
                color:
                  project.icon ||
                  getLighterShade(colorPalette[index % colorPalette.length]),
                icon: "mdi:folder",
                type: "project",
                projectKey: project.projectKey,
                code: project.code,
              };
            });
          }
          break;

        case "Tags":
          if (tagList?.length > 0) {
            // Filter tagList based on selectedTags if filter is applied
            const filteredTags = filters?.selectedTags?.length
              ? tagList.filter(
                  (tag) =>
                    filters.selectedTags!.includes(tag.tagName) ||
                    filters.selectedTags!.includes(tag._id)
                )
              : tagList;

            filteredTags.forEach((tag, index) => {
              data[tag._id] = {
                id: tag._id,
                title: tag.tagName,
                color:
                  tag.color ||
                  getLighterShade(colorPalette[index % colorPalette.length]),
                icon: "mdi:tag",
                type: "tag",
                tagName: tag.tagName,
              };
            });
          }
          break;

        case "Task type":
          const taskTypes = ["Bug", "Story", "Epic"];
          const taskTypeColors = {
            Bug: "#F44336",
            Story: "#4CAF50",
            Epic: "#2196F3",
          };
          taskTypes.forEach((type) => {
            data[type.toLowerCase()] = {
              id: type.toLowerCase(),
              title: type,
              color: taskTypeColors[type as keyof typeof taskTypeColors],
              icon: "mdi:clipboard-text",
              type: "taskType",
            };
          });
          break;

        case "Due date":
          const dueDateGroups = [
            {
              id: "overdue",
              title: "Overdue",
              color: "#F44336",
              icon: "mdi:calendar-alert",
            },
            {
              id: "today",
              title: "Due Today",
              color: "#FF9800",
              icon: "mdi:calendar-today",
            },
            {
              id: "this_week",
              title: "This Week",
              color: "#2196F3",
              icon: "mdi:calendar-week",
            },
            {
              id: "next_week",
              title: "Next Week",
              color: "#4CAF50",
              icon: "mdi:calendar-week",
            },
            {
              id: "later",
              title: "Later",
              color: "#9E9E9E",
              icon: "mdi:calendar-clock",
            },
            {
              id: "no_due_date",
              title: "No Due Date",
              color: "#607D8B",
              icon: "mdi:calendar-blank",
            },
          ];
          dueDateGroups.forEach((group) => {
            data[group.id] = {
              ...group,
              type: "dueDate",
            };
          });
          break;

        // Add Start Date grouping case
        case "Start date":
          const startDateGroups = [
            {
              id: "overdue_start",
              title: "Overdue Start",
              color: "#F44336",
              icon: "mdi:calendar-alert",
            },
            {
              id: "today_start",
              title: "Starting Today",
              color: "#FF9800",
              icon: "mdi:calendar-today",
            },
            {
              id: "this_week_start",
              title: "Starting This Week",
              color: "#2196F3",
              icon: "mdi:calendar-week",
            },
            {
              id: "next_week_start",
              title: "Starting Next Week",
              color: "#4CAF50",
              icon: "mdi:calendar-week",
            },
            {
              id: "later_start",
              title: "Starting Later",
              color: "#9E9E9E",
              icon: "mdi:calendar-clock",
            },
            {
              id: "no_start_date",
              title: "No Start Date",
              color: "#607D8B",
              icon: "mdi:calendar-blank",
            },
          ];
          startDateGroups.forEach((group) => {
            data[group.id] = {
              ...group,
              type: "startDate",
            };
          });
          break;

        default:
          break;
      }

      state.groupingData = data;
    },
    setActiveSprintIds: (state, action: PayloadAction<string[]>) => {
      state.activeSprintIds = action.payload;
    },
    setActiveSprints: (state, action: PayloadAction<any[]>) => {
      state.activeSprints = action.payload;
    },
    setBacklogViewType: (state, action: PayloadAction<string>) => {
      state.backlogViewType = action.payload;
    },
    setAllSprints: (state, action: PayloadAction<any[]>) => {
      state.allSprints = action.payload;
    },
    setSprintData: (state, action: PayloadAction<any | null>) => {
      state.selectedSprintData = action.payload;
    },
  },
});

export const {
  // Core Actions
  setSelectedItem,
  setViewType,
  setSearchQuery,
  setDebouncedSearchQuery,
  setCreateTask,
  setDueDateDateForCalendarView,
  setSelectedColumnStatus,
  // Filter Actions
  setSelectedTags,
  toggleTag,
  setSelectedAvatars,
  toggleAvatar,
  setSelectedStatuses,
  setSelectedPriorities,
  setSelectedProjects, // Export the new action
  setDueDateFilter,
  setStartDateFilter,
  setUpdatedDateFilter,
  clearAllFilters,
  setIsRecurring,
  setSelectedCreatedBy,
  // Grouping Actions
  setGroupBy,
  setSubGroupBy,
  setSortOrder,
  setShowGroupOptions,

  // UI Actions
  setDrawerOpen,
  setMinimizedColumn,
  toggleExpandedDetailPanel,

  // API Data Actions
  setStatusList,
  setProjectDetails,
  setListOfBugs,
  setListCount,
  setTagList,
  setCompanyMemberData,
  setProjects,

  // Board Data Actions
  setColumns,
  updateColumn,
  setColumnOrder,
  setSubGroupData,
  updateSubGroupData,
  setSubGroupColumnData,
  setSubGroupLoadingState,
  setGroupingData,
  setSubGroupingData,

  // Loading Actions
  setLoadingStatus,
  setInitialLoading,
  setIsBugsLoading,
  setIsBugsFetching,

  // Pagination Actions
  setPagination,
  setHasMore,

  // Form Actions
  setIsAddingColumn,
  setNewColumnTitle,
  setDeleteConfirmModal,

  // Technical Actions
  setIsDragging,
  setScrollPosition,

  // Task Actions
  updateTask,

  // Reset Actions
  resetBoardState,
  //CreateGroupingData
  createSubGroupingData,
  createGroupingData,

  //Sprint
  setActiveSprintIds,
  setActiveSprints,
  setAllSprints,
  setSprintData,
  //Backlog
  setBacklogViewType,
} = boardSlice.actions;

export default boardSlice.reducer;
